import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useMe } from "../../../hooks/queries";
import { LocationData } from "../../../models/LocationData";
import DataSvc from "../../../services/dataSvc";
import { Checkbox } from "../../FormElement/Checkbox";

import "./styles.scss";

interface LocationSelectionModalProps {
  selectedLocations: LocationData[];
  onClose: () => void;
  onApply: (locations: LocationData[]) => void;
  previousSelectedIds?: number[];
}

const LocationSelectionModal: React.FC<LocationSelectionModalProps> = ({
  selectedLocations = [],
  onClose,
  onApply,
  previousSelectedIds = [],
}) => {
  const { t: _t } = useTranslation();
  const t = useCallback(
    (key: string) => _t(`facilityDetailsPage.interactiveLineChart.${key}`),
    [_t]
  );

  const { data: me } = useMe();
  const organizationId = me?.organization?.id;

  const [locations, setLocations] = useState<LocationData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");

  const initialSelectedIds = useMemo(() => {
    const currentSelectedIds = selectedLocations.map((loc) => loc.id);
    return new Set([...currentSelectedIds, ...previousSelectedIds]);
  }, [selectedLocations, previousSelectedIds]);

  const [selectedLocationIds, setSelectedLocationIds] =
    useState<Set<number>>(initialSelectedIds);

  useEffect(() => {
    const fetchLocations = async () => {
      if (!organizationId) return;

      setIsLoading(true);
      setError(null);

      try {
        const response = await DataSvc.getLocations(organizationId);
        if (response && Array.isArray(response.list)) {
          setLocations(response.list);
        } else {
          setError("Unable to retrieve location data");
        }
      } catch (err) {
        setError("Error occurred while retrieving location data");
      } finally {
        setIsLoading(false);
      }
    };

    fetchLocations();
  }, [organizationId]);

  const filteredLocations = useMemo(() => {
    if (!searchTerm.trim()) return locations;

    const searchLower = searchTerm.toLowerCase();
    return locations.filter((location) => {
      const locationName = location.name || "";
      const locationCity = location.city || "";
      const locationPrefecture = location.prefecture || "";
      return (
        locationName.toLowerCase().includes(searchLower) ||
        locationCity.toLowerCase().includes(searchLower) ||
        locationPrefecture.toLowerCase().includes(searchLower)
      );
    });
  }, [locations, searchTerm]);

  const handleSelectionChange = (locationId: number) => {
    const newSelected = new Set(selectedLocationIds);

    if (newSelected.has(locationId)) {
      newSelected.delete(locationId);
    } else {
      newSelected.add(locationId);
    }

    setSelectedLocationIds(newSelected);
  };

  const handleSelectAll = () => {
    if (filteredLocations.length === 0) return;

    const allIds = new Set(filteredLocations.map((loc) => loc.id));
    setSelectedLocationIds(allIds);
  };

  const handleClearSelection = () => {
    setSelectedLocationIds(new Set());
  };

  const handleApply = () => {
    const selectedLocations = locations.filter((location) =>
      selectedLocationIds.has(location.id)
    );
    onApply(selectedLocations);
  };

  return (
    <div className="location-selection-modal-overlay" onClick={onClose}>
      <div
        className="location-selection-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="top-title">
          <div className="titles">{t("locationSelection")}</div>
          <div className="rights">
            <button className="btn btn-border" onClick={handleSelectAll}>
              {t("selectAll")}
            </button>
            <button className="btn btn-border" onClick={handleClearSelection}>
              {t("clear")}
            </button>
          </div>
        </div>

        <div className="search-container">
          <input
            type="text"
            className="search-input"
            placeholder={t("searchPlaceholder")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <span className="search-icon"></span>
        </div>

        <div className="location-list">
          {isLoading && (
            <div className="loading-container">
              <div className="loading-spinner"></div>
            </div>
          )}

          {!isLoading && error && <div className="error-message">{error}</div>}

          {!isLoading && !error && filteredLocations.length === 0 && (
            <div className="no-results">{t("noSearchResults")}</div>
          )}

          {!isLoading && !error && filteredLocations.length > 0 && (
            <ul>
              {filteredLocations.map((location) => (
                <li key={location.id}>
                  <label>
                    <Checkbox
                      id={`location-${location.id}`}
                      checked={selectedLocationIds.has(location.id)}
                      onChange={() => handleSelectionChange(location.id)}
                    />
                    {location.name}
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bottom-btns">
          <button className="btn btn-border" onClick={onClose}>
            {t("cancel")}
          </button>
          <button
            className="btn btn-blue"
            onClick={handleApply}
            disabled={selectedLocationIds.size === 0}
          >
            {t("apply")}
          </button>
        </div>

        <div className="btn-close" onClick={onClose}></div>
      </div>
    </div>
  );
};

export default LocationSelectionModal;
